/**
 * anonLinks — localStorage-стораж anon-ссылок, созданных без логина
 * через TryItSection (inbox-132).
 *
 * После signup'а юзер может claim'нуть свои anon-ссылки (POST
 * /api/v1/links/claim { slug }). После claim'а ссылка удаляется из
 * локального стораджа.
 *
 * Schema (LS key `sl_anon_links`):
 *   [{ slug, short_url, target_url, created_at, expires_at? }]
 *
 * Storage is best-effort. Если LS недоступен (private mode, quota) —
 * silently no-op.
 */

const LS_KEY = 'sl_anon_links';

export interface AnonLink {
  slug: string;
  short_url: string;
  target_url: string;
  created_at: string;
  expires_at?: string | null;
}

function safeRead(): AnonLink[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is AnonLink =>
        x !== null &&
        typeof x === 'object' &&
        typeof x.slug === 'string' &&
        typeof x.short_url === 'string' &&
        typeof x.target_url === 'string' &&
        typeof x.created_at === 'string',
    );
  } catch {
    return [];
  }
}

function safeWrite(items: AnonLink[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  } catch {
    /* no-op: quota / private mode / SSR */
  }
}

/**
 * Сохранить новую anon-ссылку. Если slug уже встречается — обновляем.
 * Возвращает финальный список (после dedup).
 */
export function rememberAnonLink(link: AnonLink): AnonLink[] {
  if (typeof localStorage === 'undefined') return [];
  const current = safeRead();
  const next = current.filter((l) => l.slug !== link.slug);
  next.unshift(link);
  safeWrite(next);
  return next;
}

/**
 * Прочитать все локальные anon-ссылки (newest first).
 */
export function loadAnonLinks(): AnonLink[] {
  if (typeof localStorage === 'undefined') return [];
  return safeRead();
}

/**
 * Очистить весь сторадж. Вызывается после успешного bulk-claim'а
 * либо вручную из настроек.
 */
export function clearAnonLinks(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* no-op */
  }
}

/**
 * Удалить одну ссылку по slug'у. Используется после успешного
 * claim'а — backend подтвердил, локальную копию убираем.
 */
export function forgetAnonLink(slug: string): AnonLink[] {
  if (typeof localStorage === 'undefined') return [];
  const filtered = safeRead().filter((l) => l.slug !== slug);
  safeWrite(filtered);
  return filtered;
}
